import { OWNER_FIELDS } from '../Contracts/Activity.types'

import type { ActivityOwnerKind, ActivityRecord } from '../Contracts/Activity.types'

/**
 * Uuid зв'язку з того, що тримає форма: вкладений об'єкт (`{uuid, '@id'}`),
 * IRI (`/services/clients/019f…`) або сам uuid. Нічого — `null`.
 */
export const relationUuidOf = (value: unknown): string | null => {
    if (value !== null && typeof value === 'object') {
        const record = value as Record<string, unknown>

        if (typeof record['uuid'] === 'string' && record['uuid'] !== '') return record['uuid']

        return relationUuidOf(record['@id'])
    }

    if (typeof value === 'string' && value !== '') return value.split('/').filter(Boolean).pop() ?? null

    return null
}

/**
 * Чия справа: вибір перемикача форми, а без нього — за даними (є перевізник —
 * «Перевізник», інакше «Клієнт»; легасі `ActivityCrud.jsx:24-41`).
 */
export const ownerKindOf = (values: Pick<ActivityRecord, 'ownerType' | 'carrier'> | null | undefined): ActivityOwnerKind => {
    if (values?.ownerType === 'client' || values?.ownerType === 'carrier') return values.ownerType

    return relationUuidOf(values?.carrier) !== null ? 'carrier' : 'client'
}

/**
 * Uuid, якого немає ні в кого: фільтр, що не знаходить жодного контакту.
 * Потрібен, поки власника не обрано, — інакше селект запропонував би контакти
 * всіх клієнтів одразу.
 */
export const NO_OWNER_UUID = '00000000-0000-0000-0000-000000000000'

/**
 * Фільтр селекта контакту (`SPEC-011` §8.4, К-9, Р1): лише контакти обраного
 * власника — `client.uuid = <обраний>` (`carrier.uuid` для перевізника).
 *
 * У легасі це робив `filter` селекта, і контакт іншого клієнта лишався
 * обраним після зміни клієнта; тепер бек ще й відхиляє чужий контакт (І5), а
 * зміна власника скидає контакт (`ActivityOwnerFields`).
 */
export const ownerContactFilter = (
    kind: ActivityOwnerKind,
    values: Pick<ActivityRecord, 'client' | 'carrier'> | null | undefined
): Record<string, string> => {
    const owner = OWNER_FIELDS[kind].owner

    return { [`${owner}.uuid`]: relationUuidOf(values?.[owner]) ?? NO_OWNER_UUID }
}

/** Ядрове поле селекта так, як його бачить `depFn`. */
interface ContactFieldLike {
    inputProps?: Record<string, unknown>
    mergeInputProps?: (props: Record<string, unknown>) => void
}

interface ContactDepFnArgs {
    field: ContactFieldLike
    formContext: { contextKey?: string; values?: ActivityRecord }
}

/**
 * `depFn` поля контакту: ядро кличе його на кожну зміну значень форми, і
 * фільтр селекта йде слідом за обраним власником.
 *
 * ⚠️ Фільтр — об'єкт: ядро порівнює такі пропси за посиланням і без перевірки
 * перезаписувало б їх на кожну літеру в сусідньому полі.
 *
 * Повертає `undefined`, а не `false`: `false` з `depFn` ядро читає як
 * «сховати поле». Видимість контакту вирішує шаблон.
 */
export const filterContactsOfOwner =
    (kind: ActivityOwnerKind) =>
    ({ field, formContext }: ContactDepFnArgs): void => {
        if (formContext.contextKey === 'filterFormContext') return

        const next = ownerContactFilter(kind, formContext.values)
        const key = Object.keys(next)[0] ?? ''
        const current = field.inputProps?.['filter'] as Record<string, unknown> | undefined

        if (current?.[key] === next[key]) return

        field.mergeInputProps?.({ filter: next })
    }
